'use client';

import { c as _c } from "react/compiler-runtime";
import type { GetPositionsRow } from '@morfi/backend-sdk-v2/client';
import { isI64 } from '@morfi/market-math';
import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import { OptimizedImage } from '@/components/images/optimized-image';
import { AnimatedBigInt } from '@/components/ui/animated-bigint';
import { Skeleton } from '@/components/ui/app-skeleton';
import { DataListItem, type DataListItemMetric } from '@/components/ui/data-list-item';
import { onEvent } from '@/events';
import { useOutcomeColor } from '@/hooks/chart/use-outcome-color';
import type { UserPortfolioPositionDisplayRow } from '@/hooks/positions/user-portfolio/display-types';
import { useIntentRoutePrefetch } from '@/hooks/use-intent-route-prefetch';
import { useFormattedCurrency } from '@/lib/currency';
import { pnlColorClass } from '@/lib/market/position-pnl';
import { useMarketDetailPrefetchIntent } from '@/lib/sdk/prefetch';
import { DURATION_POSITION_SMALL_MS } from '@/lib/ui/easing';
import { cn } from '@/lib/ui/utils';
import { PortfolioPositionShareButton } from './portfolio-position-actions';
import { PortfolioPositionResultBadge } from './portfolio-position-result-badge';
type PositionListItemProps = {
  row: UserPortfolioPositionDisplayRow;
  mode: 'open';
  isOwnPortfolio: boolean;
} | {
  row: GetPositionsRow;
  mode: 'closed';
  isOwnPortfolio: boolean;
  claimReady?: boolean;
};
export function PortfolioPositionListItem(props) {
  const $ = _c(85);
  const {
    row,
    mode,
    isOwnPortfolio
  } = props;
  const t = useTranslations("profile.stats.positions");
  const tFee = useTranslations("trade.fees");
  const {
    formatD6,
    formatD6Signed
  } = useFormattedCurrency();
  const label = row.outcomeLabel ?? "";
  let t0;
  if ($[0] !== label || $[1] !== row.outcomeIndex) {
    t0 = {
      title: label,
      index: row.outcomeIndex
    };
    $[0] = label;
    $[1] = row.outcomeIndex;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const color = useOutcomeColor(t0);
  const winner = row.positionType === "winner";
  const href = winner ? `/winners/${row.outcomeMint}` : `/markets/${row.market}`;
  const intent = useIntentRoutePrefetch(href, useMarketDetailPrefetchIntent(winner ? null : row.market).onMouseEnter);
  let currentValueMetric;
  let realisedMetric;
  let t1;
  let unrealised;
  if ($[3] !== formatD6 || $[4] !== formatD6Signed || $[5] !== mode || $[6] !== props.mode || $[7] !== props.row || $[8] !== row.costBasisD6 || $[9] !== row.realizedCostBasisD6 || $[10] !== row.realizedPenaltyD6 || $[11] !== row.realizedPnlD6 || $[12] !== t || $[13] !== tFee) {
    const realised = moneyWire(row.realizedPnlD6);
    const value = props.mode === "open" ? props.row.displayMark.currentValueD6 : null;
    unrealised = props.mode === "open" ? props.row.displayMark.unrealisedPnlD6 : null;
    const invested = moneyWire(mode === "open" ? row.costBasisD6 : row.realizedCostBasisD6);
    const penalty = moneyWire(row.realizedPenaltyD6);
    let t2;
    if ($[18] !== t) {
      t2 = t("value");
      $[18] = t;
      $[19] = t2;
    } else {
      t2 = $[19];
    }
    let t3;
    if ($[20] !== formatD6 || $[21] !== value) {
      t3 = amount(value, formatD6);
      $[20] = formatD6;
      $[21] = value;
      $[22] = t3;
    } else {
      t3 = $[22];
    }
    let t4;
    if ($[23] !== t2 || $[24] !== t3) {
      t4 = {
        key: "value",
        label: t2,
        value: t3
      };
      $[23] = t2;
      $[24] = t3;
      $[25] = t4;
    } else {
      t4 = $[25];
    }
    currentValueMetric = t4;
    let t5;
    if ($[26] !== t) {
      t5 = t("realised");
      $[26] = t;
      $[27] = t5;
    } else {
      t5 = $[27];
    }
    const t6 = amount(realised, formatD6Signed, true);
    let t7;
    if ($[28] !== t5 || $[29] !== t6) {
      t7 = {
        key: "realised",
        label: t5,
        value: t6
      };
      $[28] = t5;
      $[29] = t6;
      $[30] = t7;
    } else {
      t7 = $[30];
    }
    realisedMetric = t7;
    let t8;
    if ($[31] !== t) {
      t8 = t("invested");
      $[31] = t;
      $[32] = t8;
    } else {
      t8 = $[32];
    }
    const t9 = amount(invested, formatD6);
    let t10;
    if ($[33] !== t8 || $[34] !== t9) {
      t10 = {
        key: "invested",
        label: t8,
        value: t9
      };
      $[33] = t8;
      $[34] = t9;
      $[35] = t10;
    } else {
      t10 = $[35];
    }
    let t11;
    if ($[36] !== currentValueMetric || $[37] !== formatD6Signed || $[38] !== mode || $[39] !== t || $[40] !== unrealised) {
      t11 = mode === "open" ? [currentValueMetric, {
        key: "unrealised",
        label: t("unrealised"),
        value: amount(unrealised, formatD6Signed, true)
      }] : [];
      $[36] = currentValueMetric;
      $[37] = formatD6Signed;
      $[38] = mode;
      $[39] = t;
      $[40] = unrealised;
      $[41] = t11;
    } else {
      t11 = $[41];
    }
    t1 = [t10, ...t11, realisedMetric, ...(penalty !== null && penalty > BigInt(0) ? [{
      key: "penalty",
      label: tFee("penalty_fee"),
      value: amount(penalty, formatD6)
    }] : [])];
    $[3] = formatD6;
    $[4] = formatD6Signed;
    $[5] = mode;
    $[6] = props.mode;
    $[7] = props.row;
    $[8] = row.costBasisD6;
    $[9] = row.realizedCostBasisD6;
    $[10] = row.realizedPenaltyD6;
    $[11] = row.realizedPnlD6;
    $[12] = t;
    $[13] = tFee;
    $[14] = currentValueMetric;
    $[15] = realisedMetric;
    $[16] = t1;
    $[17] = unrealised;
  } else {
    currentValueMetric = $[14];
    realisedMetric = $[15];
    t1 = $[16];
    unrealised = $[17];
  }
  const metrics = t1;
  let t2;
  if ($[42] !== isOwnPortfolio || $[43] !== row.market || $[44] !== row.outcomeIndex) {
    t2 = function recordClick() {
      if (isOwnPortfolio) {
        onEvent("portfolio_position_sell_clicked", {
          marketPubkey: row.market,
          outcomeIndex: row.outcomeIndex
        });
      }
    };
    $[42] = isOwnPortfolio;
    $[43] = row.market;
    $[44] = row.outcomeIndex;
    $[45] = t2;
  } else {
    t2 = $[45];
  }
  const recordClick = t2;
  const t3 = `${label} · ${row.marketTitle ?? row.market}`;
  let t4;
  if ($[46] !== label) {
    t4 = <span className="truncate">{label}</span>;
    $[46] = label;
    $[47] = t4;
  } else {
    t4 = $[47];
  }
  let t5;
  if ($[48] !== row.resultState) {
    t5 = <PortfolioPositionResultBadge resultState={row.resultState} />;
    $[48] = row.resultState;
    $[49] = t5;
  } else {
    t5 = $[49];
  }
  let t6;
  if ($[50] !== t4 || $[51] !== t5) {
    t6 = <span className="flex min-w-0 items-center gap-2">{t4}{t5}</span>;
    $[50] = t4;
    $[51] = t5;
    $[52] = t6;
  } else {
    t6 = $[52];
  }
  const t7 = row.marketTitle ?? row.market;
  let t8;
  if ($[53] !== color.hue || $[54] !== row.market || $[55] !== row.outcomeImageNsfw || $[56] !== row.outcomeImageUrl || $[57] !== row.outcomeIndex) {
    t8 = <OptimizedImage variant="thumbnail" entity={{
      kind: "outcome",
      marketPubkey: row.market,
      outcomeIndex: row.outcomeIndex,
      hue: color.hue,
      imageUrl: row.outcomeImageUrl,
      nsfw: row.outcomeImageNsfw
    }} alt="" size="size-12" rounded="rounded-control" preview={false} />;
    $[53] = color.hue;
    $[54] = row.market;
    $[55] = row.outcomeImageNsfw;
    $[56] = row.outcomeImageUrl;
    $[57] = row.outcomeIndex;
    $[58] = t8;
  } else {
    t8 = $[58];
  }
  let t9;
  if ($[59] !== color.text) {
    t9 = cn("size-3 rounded-full bg-current", color.text);
    $[59] = color.text;
    $[60] = t9;
  } else {
    t9 = $[60];
  }
  let t10;
  if ($[61] !== t9) {
    t10 = <span className={t9} />;
    $[61] = t9;
    $[62] = t10;
  } else {
    t10 = $[62];
  }
  const t11 = mode === "open" ? currentValueMetric : realisedMetric;
  let t12;
  if ($[63] !== href || $[64] !== label || $[65] !== mode || $[66] !== row.market || $[67] !== row.marketTitle || $[68] !== row.outcomeIndex || $[69] !== row.realizedPnlD6 || $[70] !== row.wallet || $[71] !== unrealised) {
    t12 = row.wallet.length > 0 ? <PortfolioPositionShareButton destinationPath={href} marketPubkey={row.market} marketTitle={row.marketTitle ?? row.market} walletPubkey={row.wallet} outcomeIndex={row.outcomeIndex} outcomeTitle={label} closed={mode === "closed"} heroPnlD6={mode === "open" ? unrealised?.toString() : row.realizedPnlD6} /> : undefined;
    $[63] = href;
    $[64] = label;
    $[65] = mode;
    $[66] = row.market;
    $[67] = row.marketTitle;
    $[68] = row.outcomeIndex;
    $[69] = row.realizedPnlD6;
    $[70] = row.wallet;
    $[71] = unrealised;
    $[72] = t12;
  } else {
    t12 = $[72];
  }
  let t13;
  if ($[73] !== href || $[74] !== intent || $[75] !== metrics || $[76] !== recordClick || $[77] !== t10 || $[78] !== t11 || $[79] !== t12 || $[80] !== t3 || $[81] !== t6 || $[82] !== t7 || $[83] !== t8) {
    t13 = <DataListItem testId="portfolio-position-list-item" href={href} intent={intent} onClick={recordClick} ariaLabel={t3} primaryText={t6} secondaryText={t7} image={t8} imageBadge={t10} metrics={metrics} compactMetric={t11} action={t12} />;
    $[73] = href;
    $[74] = intent;
    $[75] = metrics;
    $[76] = recordClick;
    $[77] = t10;
    $[78] = t11;
    $[79] = t12;
    $[80] = t3;
    $[81] = t6;
    $[82] = t7;
    $[83] = t8;
    $[84] = t13;
  } else {
    t13 = $[84];
  }
  return t13;
}
function amount(value: bigint | null, format: (wire: string) => string, signed: boolean = false): ReactNode {
  if (value === null) return <Skeleton className="h-5 w-16" />;
  return <span className={signed ? pnlColorClass(value) : undefined}>
      <AnimatedBigInt value={value} duration={DURATION_POSITION_SMALL_MS} format={(current: bigint): string => format(current.toString())} />
    </span>;
}
function moneyWire(value: string): bigint | null {
  if (!/^-?(?:0|[1-9]\d*)$/.test(value)) return null;
  const parsed = BigInt(value);
  return isI64(parsed) ? parsed : null;
}